import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Button, MenuItem, Skeleton, TextField } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { BILL_ALLOCATION_STATE as ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { formatExtractedMoney } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import { AllocationSpikeReview } from './AllocationSpikeReview';
import { AllocationSuggestionEditForm } from './AllocationSuggestionEditForm';
import { ChargeOnDateField } from './ChargeOnDateField';
import useStyles from './styles';
import { ALLOCATION_METHOD_LABELS, formatPercent, getOverlapMessage, getSkipMessage, getTablePartsLine, getTableUtilityLabel, hasOverlappingBills, isAwaitingSpikeReview } from './suggestionModel';
const SKELETON_ROWS = 4;
const AWAITING_STATES_HINT = "Checking where this table's allocation stands.";
function SummaryItem(_ref) {
  let label = _ref.label,
    value = _ref.value;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.summaryItem
  }, /*#__PURE__*/React.createElement("span", null, label), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, value));
}
function StatusChip(_ref2) {
  let table = _ref2.table;
  const classes = useStyles();
  const _ref3 = (() => {
      if (table.isApproved) return {
        className: classes.statusChipDone,
        label: 'Allocated'
      };
      if (table.allocationState === ALLOCATION_STATE.allocating) return {
        className: '',
        label: 'Allocating…'
      };
      // Allocating stops here: nothing is under way until somebody approves it (LB-6052).
      if (table.allocationState === ALLOCATION_STATE.awaitingApproval) {
        return {
          className: '',
          label: 'Awaiting approval'
        };
      }
      if (isAwaitingSpikeReview(table)) return {
        className: classes.statusChipMuted,
        label: 'Needs review'
      };
      // Reviewed, it is waiting on approval like any other worked-out table.
      if (table.allocationState === ALLOCATION_STATE.needsReview) return {
        className: '',
        label: 'Awaiting approval'
      };
      if (table.allocationState === ALLOCATION_STATE.skipped) return {
        className: classes.statusChipMuted,
        label: 'Skipped'
      };
      if (table.allocationState === ALLOCATION_STATE.failed) return {
        className: classes.statusChipMuted,
        label: 'Could not allocate'
      };
      if (table.needsProperty || table.needsUtility) return {
        className: classes.statusChipMuted,
        label: 'Needs a match'
      };
      if (!table.isComputable) return {
        className: classes.statusChipMuted,
        label: 'Not allocated'
      };
      if (hasOverlappingBills(table)) return {
        className: classes.statusChipMuted,
        label: 'Already billed'
      };
      if (table.isLoading) return {
        className: '',
        label: 'Calculating…'
      };
      // Not "Pending approval": approving comes after allocating, and a table nobody has allocated is a step short of it.
      return {
        className: '',
        label: 'Ready to allocate'
      };
    })(),
    className = _ref3.className,
    label = _ref3.label;
  return /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(className)
  }, label);
}

/**
 * Approve, unless the table was allocated in Apply Billing: it is approved there, and this page's
 * approve would leave it alone (LB-5952).
 */
function getApproveAction(table) {
  if (table.isMadeInApplyBilling) {
    return {
      hint: 'This table was allocated in Apply Billing, so it is approved there.',
      isDisabled: true,
      label: 'Approve'
    };
  }
  return {
    action: 'approve',
    isDisabled: false,
    label: 'Approve'
  };
}

/**
 * A service scheduled for a later month (LB-6009): Allocate is still offered, and the service takes it once that month
 * opens, saying so when it hasn't. "Use it this cycle", above, moves it now.
 */
function scheduledHint(table) {
  if (!table.scheduledFor) return undefined;
  const month = moment(table.scheduledFor, 'YYYY-MM-DD').format('MMMM YYYY');
  return "Scheduled for ".concat(month, ": Allocate takes it once that month has opened, or use it this cycle above.");
}

/**
 * What the table's button offers, given where the service has got to. Allocating and approving are
 * two clicks (LB-6052): Allocate works the table out and stops at "Awaiting approval"; Approve is
 * what charges the residents on it.
 */
function getTableAction(table) {
  switch (table.allocationState) {
    case ALLOCATION_STATE.allocated:
      return {
        isDisabled: true,
        label: 'Allocated'
      };
    case ALLOCATION_STATE.allocating:
      return {
        isDisabled: true,
        label: 'Allocating…'
      };
    case ALLOCATION_STATE.awaitingApproval:
      return getApproveAction(table);
    case ALLOCATION_STATE.needsReview:
      // Reviewed, the hold is lifted and the table waits on its approval like any other worked-out one.
      if (!isAwaitingSpikeReview(table)) return getApproveAction(table);
      return {
        hint: 'This bill rose sharply on the last one. Review the comparison with the previous cycles, then confirm you have, to approve it.',
        isDisabled: true,
        label: 'Needs review'
      };
    case ALLOCATION_STATE.skipped:
      return {
        isDisabled: true,
        label: 'Skipped'
      };
    case ALLOCATION_STATE.failed:
      return {
        action: 'allocate',
        hint: table.allocationError || scheduledHint(table),
        isDisabled: false,
        label: 'Try again'
      };
    default:
      return {
        action: 'allocate',
        hint: scheduledHint(table),
        isDisabled: false,
        label: 'Allocate'
      };
  }
}
function getTargetMessage(table, utilityOptions) {
  if (table.needsProperty) {
    return 'The address on the bill does not match a single property on this company. Pick the property to allocate against.';
  }
  if (!utilityOptions.length) {
    return 'This property has no utilities set up, so there is nothing to allocate this service against. Set the utility up on the property, or pick another property.';
  }

  // Allocating only takes the utility the bill was matched to, so there is nothing to pick here: a pick could only be
  // refused (LB-6067). What does help is said instead.
  if (table.hasUtilityMatch) {
    return "More than one utility on this property covers this service, usually a meter per building. Save this bill's account number against the right one under the property's account numbers, and bills with that number will match by themselves. Allocate this one in Apply Billing.";
  }
  return "No utility on this property covers this service, so it can't be allocated from here. Set it up on the property, or allocate the bill in Apply Billing.";
}

/**
 * Shown when the bill cannot be tied to a single property utility on its own. The property stays
 * editable either way, because an address that matched the wrong property leads here too. The utility
 * is not offered: it is what the bill was matched to, or nothing it can be allocated against (LB-6067).
 */
function TargetPicker(_ref4) {
  let onSelectProperty = _ref4.onSelectProperty,
    propertyOptions = _ref4.propertyOptions,
    table = _ref4.table,
    utilityOptions = _ref4.utilityOptions;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.picker
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.message
  }, getTargetMessage(table, utilityOptions)), /*#__PURE__*/React.createElement("div", {
    className: classes.pickerFields
  }, /*#__PURE__*/React.createElement(TextField, {
    className: classes.pickerField,
    label: "Property",
    onChange: event => onSelectProperty(event.target.value),
    select: true,
    size: "small",
    value: table.propertyId || ''
  }, propertyOptions.map(option => /*#__PURE__*/React.createElement(MenuItem, {
    key: option.id,
    value: option.id
  }, option.label)))));
}

/** Headed and divided as the Apply Billing allocation table is, the table this previews (LB-5509). */
function UnitRows(_ref5) {
  let currency = _ref5.currency,
    units = _ref5.units;
  const classes = useStyles();
  const firstUnbillable = units.findIndex(unit => !unit.isBillable);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.row, " ").concat(classes.rowHead)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.colUnit
  }, "Unit number"), /*#__PURE__*/React.createElement("span", {
    className: classes.colResident
  }, "Resident Name"), /*#__PURE__*/React.createElement("span", {
    className: classes.colOccupants
  }, "Number of occupants"), /*#__PURE__*/React.createElement("span", {
    className: classes.colFactor
  }, "Unit occupancy factor"), /*#__PURE__*/React.createElement("span", {
    className: classes.colDays
  }, "Number of days"), /*#__PURE__*/React.createElement("span", {
    className: classes.colTotal
  }, "Utility total")), units.map((unit, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: unit.key
  }, index === firstUnbillable ? /*#__PURE__*/React.createElement("div", {
    className: classes.unbillableDivider
  }, "The following residents are not billable yet") : null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.row, " ").concat(index === units.length - 1 ? classes.rowLast : '', " ").concat(unit.isBillable ? '' : classes.rowMuted)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.colUnit,
    title: unit.unit
  }, unit.unit), /*#__PURE__*/React.createElement("span", {
    className: classes.colResident,
    title: unit.resident
  }, unit.resident), /*#__PURE__*/React.createElement("span", {
    className: classes.colOccupants
  }, unit.occupants), /*#__PURE__*/React.createElement("span", {
    className: classes.colFactor
  }, Number(unit.occupancyFactor || 0).toFixed(2)), /*#__PURE__*/React.createElement("span", {
    className: classes.colDays
  }, unit.days), /*#__PURE__*/React.createElement("span", {
    className: classes.colTotal
  }, formatExtractedMoney(unit.total, currency))))));
}

/**
 * One suggested allocation table: what the allocation service would charge each unit for this
 * property and utility, before anything is saved. Shared by the Bills page and the billing cycle so
 * both show the same figures for the same bill values.
 */
export function AllocationSuggestionTable(_ref6) {
  var _table$allocatedAmoun;
  let allocateHint = _ref6.allocateHint,
    approveHint = _ref6.approveHint,
    _ref6$chargeOn = _ref6.chargeOn,
    chargeOn = _ref6$chargeOn === void 0 ? null : _ref6$chargeOn,
    currency = _ref6.currency,
    _ref6$isEditing = _ref6.isEditing,
    isEditing = _ref6$isEditing === void 0 ? false : _ref6$isEditing,
    onAllocate = _ref6.onAllocate,
    onApprove = _ref6.onApprove,
    onChangeSetting = _ref6.onChangeSetting,
    onReview = _ref6.onReview,
    onSelectProperty = _ref6.onSelectProperty,
    _ref6$propertyOptions = _ref6.propertyOptions,
    propertyOptions = _ref6$propertyOptions === void 0 ? [] : _ref6$propertyOptions,
    _ref6$setting = _ref6.setting,
    setting = _ref6$setting === void 0 ? {} : _ref6$setting,
    table = _ref6.table,
    _ref6$utilityOptions = _ref6.utilityOptions,
    utilityOptions = _ref6$utilityOptions === void 0 ? [] : _ref6$utilityOptions;
  const classes = useStyles();
  const needsTarget = table.needsProperty || table.needsUtility;
  // Once a service is allocated the figures are saved; editing them here would only disagree.
  const canEdit = isEditing && !needsTarget && !table.isAllocated;
  const hasUnits = (table.units || []).length > 0;
  const isAlreadyBilled = hasOverlappingBills(table);
  const tableAction = getTableAction(table);
  // Approving sends the charges to AppFolio with the date they have then (LB-6118): not while one is being saved. One
  // that couldn't be saved is tried again by approving, which goes ahead only if it then is.
  const isApproving = tableAction.action === 'approve';
  const isWaitingOnChargeOn = isApproving && !!(chargeOn !== null && chargeOn !== void 0 && chargeOn.isSaving);
  const chargeOnHint = (() => {
    if (!isApproving || !chargeOn) return '';
    if (chargeOn.isSaving) return 'Saving the charge on date first.';
    return chargeOn.hasUnsavedDate ? "The charge on date isn't saved yet: approving saves it first." : '';
  })();
  const handleAction = {
    allocate: onAllocate,
    approve: onApprove
  }[tableAction.action];
  const actionHint = {
    allocate: allocateHint,
    approve: approveHint
  }[tableAction.action];
  const partsLine = getTablePartsLine(table, currency);
  const renderBody = () => {
    if (needsTarget) {
      return /*#__PURE__*/React.createElement(TargetPicker, {
        onSelectProperty: onSelectProperty,
        propertyOptions: propertyOptions,
        table: table,
        utilityOptions: utilityOptions
      });
    }
    if (!table.isComputable) return /*#__PURE__*/React.createElement("div", {
      className: classes.message
    }, getSkipMessage(table.skipReason));
    if (table.hasFailed) {
      return /*#__PURE__*/React.createElement("div", {
        className: classes.message
      }, "The allocation suggestion could not be calculated. Please try again.");
    }
    if (hasUnits) return /*#__PURE__*/React.createElement(UnitRows, {
      currency: currency,
      units: table.units
    });
    if (table.isLoading) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, Array.from({
        length: SKELETON_ROWS
      }).map((item, index) => /*#__PURE__*/React.createElement("div", {
        className: classes.skeletonRow,
        key: index
      }, /*#__PURE__*/React.createElement(Skeleton, {
        height: 20,
        variant: "text"
      }))));
    }
    return /*#__PURE__*/React.createElement("div", {
      className: classes.message
    }, "This property has no units to allocate against.");
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.panel
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.titleRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.title
  }, getTableUtilityLabel(table), " allocation"), /*#__PURE__*/React.createElement(StatusChip, {
    table: table
  })), partsLine ? /*#__PURE__*/React.createElement("div", {
    className: classes.parts
  }, partsLine) : null, canEdit ? /*#__PURE__*/React.createElement(AllocationSuggestionEditForm, {
    currency: currency
    // The range picker holds its own dates, so switching tables has to remount it.
    ,
    key: table.key,
    onChange: onChangeSetting,
    setting: setting,
    table: table
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.summary
  }, /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Service dates",
    value: table.servicePeriod
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Bill amount"
    // Allocated, the figure is what the saved table holds, not what this bill reads as (LB-6015).
    ,
    value: formatExtractedMoney((_table$allocatedAmoun = table.allocatedAmount) !== null && _table$allocatedAmoun !== void 0 ? _table$allocatedAmoun : table.billAmount, currency)
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Deductions",
    value: formatPercent(table.deductionPercent)
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Total billing",
    value: formatExtractedMoney(table.totalBilling, currency)
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Recovery",
    value: formatPercent(table.recoveryPercent)
  }), table.allocationMethod ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Method",
    value: ALLOCATION_METHOD_LABELS[table.allocationMethod] || table.allocationMethod
  })) : null), chargeOn && !needsTarget ? /*#__PURE__*/React.createElement(ChargeOnDateField, {
    chargeOn: chargeOn
  }) : null, table.isShortOfBill ? /*#__PURE__*/React.createElement("div", {
    className: classes.warningBox
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.warningText
  }, "Only ".concat(formatExtractedMoney(table.allocatedAmount, currency), " of the ").concat(formatExtractedMoney(table.extractedAmount, currency), " this bill charges for ").concat(getTableUtilityLabel(table), " was allocated."))) : null, needsTarget || !isAlreadyBilled ? null : /*#__PURE__*/React.createElement("div", {
    className: classes.warningBox
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.warningText
  }, getOverlapMessage(table.overlappingBills))), table.allocationError ? /*#__PURE__*/React.createElement("div", {
    className: classes.warningBox
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.warningText
  }, table.allocationError)) : null, isEditing || table.allocationState !== ALLOCATION_STATE.needsReview ? null : /*#__PURE__*/React.createElement(AllocationSpikeReview, {
    comparison: table.comparison,
    isSaving: !!table.isReviewing,
    onReview: onReview ? () => onReview(table.key) : undefined
  }), renderBody(), isEditing || !hasUnits ? null : /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title:
    // Until the bill's allocation states are in, what this table's button will be isn't known yet (LB-6052).
    (table.isAwaitingStates ? AWAITING_STATES_HINT : null) || (isAlreadyBilled ? 'These service dates are already billed for this utility.' : tableAction.hint) || chargeOnHint || actionHint || '',
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !handleAction || tableAction.isDisabled || isAlreadyBilled || isWaitingOnChargeOn,
    onClick: () => handleAction(table.key),
    size: "small",
    variant: "text"
  }, tableAction.label)))));
}